import * as React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Unlink,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  hasRichHtmlMarkup,
  normalizeDescriptionForSave,
} from '../../lib/invitation';
import { Button } from './button';
import { Input } from './input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

function escapeHtml(text) {
  return (text || '')
    .toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function toEditorContent(value) {
  const str = (value || '').toString();
  if (!str) return '';
  if (hasRichHtmlMarkup(str)) return str;
  return str
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function ToolbarButton({ label, active, disabled, onClick, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={!!active}
          disabled={disabled}
          onClick={onClick}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--ba-body-text)] transition-colors',
            'hover:bg-[var(--ba-hover-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/40',
            'disabled:pointer-events-none disabled:opacity-40',
            active && 'bg-[var(--ba-accent-orange-subtle)] text-brand-orange'
          )}
        >
          {children}
          <span className="sr-only">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Inserisci una descrizione dell\'invito…',
  className,
  editable = true,
}) {
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right'],
        defaultAlignment: 'left',
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: toEditorContent(value),
    editable,
    editorProps: {
      attributes: {
        class: 'ba-rich-text outline-none',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(normalizeDescriptionForSave(ed.isEmpty ? '' : ed.getHTML()));
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    const incoming = toEditorContent(value);
    if (editor.isEmpty && !incoming) return;
    if (editor.getHTML() === incoming) return;
    // Avoid clobbering while typing: only sync when parent value diverges from live HTML.
    const live = normalizeDescriptionForSave(editor.isEmpty ? '' : editor.getHTML());
    const next = normalizeDescriptionForSave(value);
    if (live === next) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [value, editor]);

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  const openLinkDialog = () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href || '';
    setLinkUrl(previous);
    setLinkOpen(true);
  };

  const applyLink = () => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkOpen(false);
      return;
    }
    const href = /^(https?:|mailto:)/i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setLinkOpen(false);
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  if (!editor) {
    return (
      <div
        className={cn(
          'ba-rich-text-editor min-h-[148px] animate-pulse rounded-lg border border-[var(--ba-input-border)] bg-[var(--ba-input-bg)]',
          className
        )}
      />
    );
  }

  return (
    <>
      <div
        className={cn(
          'ba-rich-text-editor overflow-hidden rounded-lg border border-[var(--ba-input-border)] bg-[var(--ba-input-bg)] transition-colors',
          'focus-within:border-brand-orange focus-within:ring-2 focus-within:ring-brand-orange/25',
          className
        )}
      >
        <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--ba-input-border)] bg-[var(--ba-accent-orange-subtle)]/40 px-1.5 py-1">
          <ToolbarButton
            label="Grassetto"
            active={editor.isActive('bold')}
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Corsivo"
            active={editor.isActive('italic')}
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <span className="mx-1 h-5 w-px bg-[var(--ba-input-border)]" aria-hidden />
          <ToolbarButton
            label="Allinea a sinistra"
            active={editor.isActive({ textAlign: 'left' })}
            disabled={!editable}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <AlignLeft className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Allinea al centro"
            active={editor.isActive({ textAlign: 'center' })}
            disabled={!editable}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <AlignCenter className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Allinea a destra"
            active={editor.isActive({ textAlign: 'right' })}
            disabled={!editable}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <AlignRight className="h-4 w-4" />
          </ToolbarButton>
          <span className="mx-1 h-5 w-px bg-[var(--ba-input-border)]" aria-hidden />
          <ToolbarButton
            label="Elenco puntato"
            active={editor.isActive('bulletList')}
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Elenco numerato"
            active={editor.isActive('orderedList')}
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <span className="mx-1 h-5 w-px bg-[var(--ba-input-border)]" aria-hidden />
          <ToolbarButton
            label="Inserisci link"
            active={editor.isActive('link')}
            disabled={!editable}
            onClick={openLinkDialog}
          >
            <LinkIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Rimuovi link"
            disabled={!editable || !editor.isActive('link')}
            onClick={removeLink}
          >
            <Unlink className="h-4 w-4" />
          </ToolbarButton>
        </div>
        <EditorContent editor={editor} />
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inserisci link</DialogTitle>
            <DialogDescription>
              L&apos;URL verrà aperto in una nuova scheda sulla pagina pubblica dell&apos;invito.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="ba-label" htmlFor="rich-text-link-url">
              URL
            </label>
            <Input
              id="rich-text-link-url"
              type="url"
              placeholder="https://…"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setLinkOpen(false)}>
              Annulla
            </Button>
            <Button type="button" onClick={applyLink}>
              {linkUrl.trim() ? 'Applica' : 'Rimuovi link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { RichTextEditor };
